/**
 * Document ↔ CvsmDocument 翻译（M2-03）。
 *
 * io 层不认识文档模型（分层规则），`.cvsm` 模块收发的是独立的序列化契约
 * {@link CvsmDocument}；在两个模型之间来回翻译是**应用层**的职责——
 * 这正是 M1-15 注释里预留的位置。
 *
 * 当前覆盖 raster / group / adjustment 三种图层。
 * 其他图层种类在遇到时抛错而不是静默丢弃——丢内容的保存比失败更糟。
 */
import type { CvsmDocument, CvsmTile, CvsmVectorFill, CvsmVectorStroke } from '../../io/cvsm';
import type { CvsmLayer, CvsmLayerComp } from '../../io/cvsm';
import type { Adjustment, Document, Layer, LayerComp, Mat3, VectorFill, VectorStroke } from '../../document/types';
import { createDocument, layerDefaults } from '../../document/draft';
import { maskLayerIdFor } from '../commands/mask';
import { TILE, layerId, tileKey } from '../../engine/tile/key';
import type { TileStore } from '../../engine/tile/store';

/** 老文件的 shape.fill/runs[].fill 是纯色字符串——读出来时升级成新的标签形式。 */
function toVectorFill(raw: CvsmVectorFill): VectorFill {
  return typeof raw === 'string' ? { type: 'solid', color: raw } : raw;
}

function toVectorStroke(raw: CvsmVectorStroke): VectorStroke {
  return { fill: toVectorFill(raw.fill), width: raw.width };
}

function matToTuple(m: Mat3): readonly [number, number, number, number, number, number] {
  return [m.a, m.b, m.c, m.d, m.e, m.f];
}

function tupleToMat(t: readonly [number, number, number, number, number, number]): Mat3 {
  return { a: t[0], b: t[1], c: t[2], d: t[3], e: t[4], f: t[5] };
}

function layerToCvsm(layer: Layer): CvsmLayer {
  const mask = layer.mask;
  const base = {
    id: layer.id as string,
    name: layer.name,
    kind: layer.kind,
    visible: layer.visible,
    opacity: layer.opacity,
    fillOpacity: layer.fillOpacity,
    blendMode: layer.blendMode,
    clipped: layer.clipped,
    transform: matToTuple(layer.transform),
    // 蒙版元数据（M3-08）；像素块以 `<层id>#mask` 为键与普通块同道收集
    ...(mask === undefined
      ? {}
      : {
          mask: {
            enabled: mask.enabled,
            linked: mask.linked,
            ...(mask.base === undefined ? {} : { base: mask.base }),
            bounds: mask.bounds,
          },
        }),
    // 图层样式（M3-14）：空数组不写入，容器里省一个字段
    ...(layer.effects.length === 0
      ? {}
      : {
          effects: layer.effects.map((effect) => ({
            kind: effect.kind,
            enabled: effect.enabled,
            params: effect.params,
          })),
        }),
  };
  if (layer.kind === 'group') {
    return {
      ...base,
      children: layer.children.map(layerToCvsm),
      passThrough: layer.passThrough,
    };
  }
  if (layer.kind === 'raster') {
    return base;
  }
  if (layer.kind === 'adjustment') {
    return {
      ...base,
      adjustment: { kind: layer.adjustment.kind, params: layer.adjustment.params },
    };
  }
  if (layer.kind === 'text') {
    // 可编辑源 + 栅格化结果双份入容器：重载后既能显示也能继续编辑（M3-08）
    return {
      ...base,
      text: {
        runs: layer.runs,
        box: layer.box,
        writingMode: layer.writingMode,
        ...(layer.warp === undefined ? {} : { warp: layer.warp }),
      },
    };
  }
  if (layer.kind === 'shape') {
    return {
      ...base,
      shape: {
        commands: layer.path.commands,
        fillRule: layer.path.fillRule,
        ...(layer.fill === undefined ? {} : { fill: layer.fill }),
        ...(layer.stroke === undefined ? {} : { stroke: layer.stroke }),
      },
    };
  }
  if (layer.kind === 'smartObject') {
    // `source` 暂不持久化（ADR-0008 补记）；raster 像素与 raster/text 同道收集
    return {
      ...base,
      smartObject: {
        bounds: layer.bounds,
        filters: layer.filters.map((f) => ({ kind: f.kind, enabled: f.enabled, params: f.params })),
      },
    };
  }
  throw new Error(
    `保存 .cvsm 尚不支持「${layer.kind}」图层（图层「${layer.name}」）。` +
      `静默丢弃比保存失败更糟，所以这里直接拒绝。`,
  );
}

/** 文档 + 像素 → 待保存的 CvsmDocument。 */
export function toCvsmDocument(document: Document, tiles: TileStore): CvsmDocument {
  const cvsmTiles: CvsmTile[] = [];
  const collectTilesOf = (id: string): void => {
    for (const tile of tiles.residentTilesOf(layerId(id), 0)) {
      if (tile.data === null) continue;
      cvsmTiles.push({ layerId: id, lod: 0, tx: tile.key.tx, ty: tile.key.ty, data: tile.data });
    }
  };
  const collect = (layer: Layer): void => {
    // 蒙版像素可以挂在任何图层种类上（M2-13）
    if (layer.mask !== undefined) collectTilesOf(maskLayerIdFor(layer.id) as string);
    if (layer.kind === 'group') {
      for (const child of layer.children) collect(child);
      return;
    }
    // 文本图层的栅格化结果、智能对象的原始像素都与 raster 同道
    if (layer.kind !== 'raster' && layer.kind !== 'text' && layer.kind !== 'smartObject') return;
    collectTilesOf(layer.id as string);
  };
  for (const child of document.root.children) collect(child);

  return {
    id: document.id,
    size: document.size,
    tileSize: TILE,
    createdAt: document.meta.createdAt,
    modifiedAt: document.meta.modifiedAt,
    root: layerToCvsm(document.root),
    tiles: cvsmTiles,
    ...(document.layerComps.length === 0
      ? {}
      : { layerComps: document.layerComps.map(layerCompToCvsm) }),
  };
}

function layerCompToCvsm(comp: LayerComp): CvsmLayerComp {
  return {
    id: comp.id,
    name: comp.name,
    entries: comp.entries.map((entry) => ({
      layerId: entry.layerId as string,
      visible: entry.visible,
      opacity: entry.opacity,
      transform: matToTuple(entry.transform),
      effectsEnabled: entry.effectsEnabled,
    })),
  };
}

function cvsmToLayerComp(comp: CvsmLayerComp): LayerComp {
  return {
    id: comp.id,
    name: comp.name,
    entries: comp.entries.map((entry) => ({
      layerId: layerId(entry.layerId),
      visible: entry.visible,
      opacity: entry.opacity,
      transform: tupleToMat(entry.transform),
      effectsEnabled: entry.effectsEnabled,
    })),
  };
}

/** 读入的 CvsmDocument → 文档，块像素写进 tiles。 */
export function fromCvsmDocument(
  loaded: {
    readonly manifest: {
      readonly id: string;
      readonly size: { readonly width: number; readonly height: number };
      readonly createdAt: string;
      readonly modifiedAt: string;
      readonly root: CvsmLayer;
      readonly layerComps?: readonly CvsmLayerComp[];
    };
    readonly tiles: readonly CvsmTile[];
  },
  tiles: TileStore,
  now: () => string,
): Document {
  const manifest = loaded.manifest;
  for (const tile of loaded.tiles) {
    if (tile.lod !== 0) continue; // LOD 金字塔按需重建，不落盘恢复
    tiles.set(tileKey(layerId(tile.layerId), tile.tx, tile.ty), tile.data);
  }

  const base = createDocument({
    id: manifest.id,
    width: manifest.size.width,
    height: manifest.size.height,
    createdAt: manifest.createdAt === '' ? now() : manifest.createdAt,
  });

  const rebuild = (node: CvsmLayer): Layer => {
    const common = {
      ...layerDefaults(layerId(node.id), node.name),
      visible: node.visible,
      opacity: node.opacity,
      fillOpacity: node.fillOpacity,
      blendMode: node.blendMode as Layer['blendMode'],
      clipped: node.clipped,
      transform: tupleToMat(node.transform),
      effects: (node.effects ?? []).map((effect) => ({
        kind: effect.kind as Layer['effects'][number]['kind'],
        enabled: effect.enabled,
        params: effect.params,
      })),
      ...(node.mask === undefined
        ? {}
        : {
            mask: {
              tiles: tiles.handle(maskLayerIdFor(layerId(node.id))),
              bounds: node.mask.bounds,
              linked: node.mask.linked,
              enabled: node.mask.enabled,
              ...(node.mask.base === undefined ? {} : { base: node.mask.base }),
            },
          }),
    };
    if (node.kind === 'group') {
      return {
        ...common,
        kind: 'group',
        children: (node.children ?? []).map(rebuild),
        passThrough: node.passThrough ?? true,
        collapsed: false,
      };
    }
    if (node.kind === 'adjustment' && node.adjustment !== undefined) {
      return {
        ...common,
        kind: 'adjustment',
        adjustment: {
          // io 层只验形状；语义上未知的 kind 在合成时按无操作处理（resolve 是鸭子类型）
          kind: node.adjustment.kind as Adjustment['kind'],
          params: node.adjustment.params,
        },
      };
    }
    if (node.kind === 'text' && node.text !== undefined) {
      return {
        ...common,
        kind: 'text',
        runs: node.text.runs.map((run) => ({
          text: run.text,
          fontFamily: run.fontFamily,
          fontSize: run.fontSize,
          fill: toVectorFill(run.fill),
          letterSpacing: run.letterSpacing,
          lineHeight: run.lineHeight,
          ...(run.stroke === undefined ? {} : { stroke: toVectorStroke(run.stroke) }),
        })),
        box: node.text.box,
        writingMode: node.text.writingMode,
        ...(node.text.warp === undefined ? {} : { warp: node.text.warp }),
      };
    }
    if (node.kind === 'shape' && node.shape !== undefined) {
      return {
        ...common,
        kind: 'shape',
        path: { commands: node.shape.commands, fillRule: node.shape.fillRule },
        ...(node.shape.fill === undefined ? {} : { fill: toVectorFill(node.shape.fill) }),
        ...(node.shape.stroke === undefined ? {} : { stroke: toVectorStroke(node.shape.stroke) }),
      };
    }
    if (node.kind === 'smartObject' && node.smartObject !== undefined) {
      return {
        ...common,
        kind: 'smartObject',
        // source 未持久化（ADR-0008 补记）：占位值，渲染路径不读它
        source: { type: 'file', bytes: new Uint8Array(0), mime: 'application/octet-stream' },
        raster: tiles.handle(layerId(node.id)),
        bounds: node.smartObject.bounds,
        filters: node.smartObject.filters.map((f) => ({ kind: f.kind, enabled: f.enabled, params: f.params })),
      };
    }
    // 未知种类按 raster 兜底读出——像素还在，比拒绝打开好；
    // 写回时 layerToCvsm 会再次把关
    return {
      ...common,
      kind: 'raster',
      tiles: tiles.handle(layerId(node.id)),
      bounds: { x: 0, y: 0, width: manifest.size.width, height: manifest.size.height },
    };
  };

  const root = rebuild(manifest.root);
  if (root.kind !== 'group') {
    throw new Error('.cvsm 的根必须是组图层。');
  }
  // 从零构造新文档，直接组装只读对象；DocumentDraft 归命令层专用
  return {
    ...base,
    root: { ...base.root, children: root.children },
    layerComps: (manifest.layerComps ?? []).map(cvsmToLayerComp),
    meta: {
      ...base.meta,
      modifiedAt: manifest.modifiedAt === '' ? now() : manifest.modifiedAt,
    },
  };
}
